/* eslint-disable @typescript-eslint/dot-notation */
import { Injectable } from '@angular/core';
import { FormGroup } from '@angular/forms';
import { Store } from '@ngrx/store';
import * as moment from 'moment';
import { BehaviorSubject, lastValueFrom, map, pairwise, startWith } from 'rxjs';
import { AppState } from 'src/app/app.reducer';
import { LocalStorageService } from 'src/app/common/services/local-storage.service';
import { SessionStorageService } from 'src/app/common/services/session-storage.service';
import * as pricesActions from '../stores/prices.actions';
import * as proposalActions from '../stores/proposal.actions';
import * as quoteActions from '../stores/quote.actions';
import { PolicyService } from './http/policy.service';
import { ProposalService } from './http/proposal.service';
import { QuotationService } from './http/quotation.service';

const PROPOSAL_HAS_MODIFICATIONS = 'hasModifications';
const PROPOSAL_HAS_NEW_DUBAI = 'hasNewDubai';
@Injectable({
  providedIn: 'root',
})
export class DataManagerService {
  private quote;

  private dubaiChangued: boolean = this.localStorageService.get(PROPOSAL_HAS_NEW_DUBAI) || false;

  private hasChangeDubai$: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(
    this.dubaiChangued
  );

  public hasChangeDubai = this.hasChangeDubai$.asObservable();

  private hasModifications: boolean =
    this.localStorageService.get(PROPOSAL_HAS_MODIFICATIONS) || false;

  private hasModifications$: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(
    this.hasModifications
  );

  currentHasModifications = this.hasModifications$.asObservable();

  constructor(
    private localStorageService: LocalStorageService,
    private sessionStorageService: SessionStorageService,
    private quotationService: QuotationService,
    private proposalService: ProposalService,
    private policyService: PolicyService,
    private store: Store<AppState>
  ) {
    this.getQuote();
  }

  private getQuote() {
    this.store.select('quote').subscribe((state) => {
      this.quote = state;
    });
  }

  public setQuote(quote = {}) {
    this.store.dispatch(quoteActions.setQuote({ quote }));
    this.localStorageService.set('currentQuote', quote);
  }

  public setPrices(prices = {}) {
    this.store.dispatch(pricesActions.setPrices({ prices }));
    this.localStorageService.set('quotePrices', prices);
  }

  public setProposal(proposal = {}) {
    this.store.dispatch(proposalActions.setProposal({ proposal }));
    this.localStorageService.set('currentProposal', proposal);
  }

  private async saveQuote() {
    const { result } = await lastValueFrom(this.quotationService.save());
    this.setQuote(result);
  }

  public async updateQuote(data = {}, id = '') {
    const { result } = await lastValueFrom(this.quotationService.update(data, id));
    this.setQuote(result);
  }

  public async calculatePrices(data = {}, id = '') {
    const { result } = await lastValueFrom(this.quotationService.calculatePrices(data, id));
    this.setPrices(result);
  }

  public async queryDocument(id: string, data = {}) {
    const { result } = await lastValueFrom(this.quotationService.queryDocument(data, id));
    this.setQuote(result);
  }

  public async saveProposal(data) {
    const { result } = await lastValueFrom(this.proposalService.save(data));
    this.setQuote(result);
  }

  public async updateProposal(data = {}, id = '') {
    const { result } = await lastValueFrom(this.proposalService.update(data, id));
    this.setProposal(result);
  }

  public async savePolicy(data = {}) {
    const { result } = await lastValueFrom(this.policyService.save(data));
    this.setQuote(result);
  }

  public async validateForm(data) {
    this.getQuote();

    // if (!this.quote?.id) await this.saveQuote();
    // else await this.updateQuote(data, this.quote.id);
  }

  public validateControlChanged(form, isGroupsArray = false, arrayName = null) {
    this.getQuote();

    let localForm = form as FormGroup;

    if (isGroupsArray) {
      localForm = form.get(arrayName) as FormGroup;
    }

    localForm.valueChanges
      .pipe(
        startWith(localForm.value),
        pairwise(),
        map(([prev, curr]) =>
          Object.keys(curr).find((key) => {
            if (Array.isArray(prev[key]) || Array.isArray(curr[key])) {
              return (
                prev[key]?.length !== curr[key]?.length ||
                prev[key]?.every((value, index) => value !== curr[key][index])
              );
            }

            return prev[key] !== curr[key];
          })
        )
      )
      .subscribe((key) => {
        if (key) {
          const control = localForm.controls[key];

          if (control.valid) {
            if (isGroupsArray) {
              let groupsArray = [];
              if (Object.prototype.hasOwnProperty.call(this.quote, arrayName)) {
                groupsArray = [...this.quote[arrayName]];
              }

              groupsArray[key] = control.value;

              const quote = { ...this.quote, [arrayName]: groupsArray };

              this.setQuote(quote);
            } else {
              const quote = { ...this.quote, [key]: control.value };
              this.setQuote(quote);
            }
          }
        }
      });
  }

  public addToPrevious() {
    this.store.dispatch(quoteActions.addToPrevious());
  }

  public setFromPreviousQuotes(): void {
    this.localStorageService.set('fromPreviousQuotes', true);
  }

  public getFromPreviousQuotes(): boolean {
    return this.localStorageService.get('fromPreviousQuotes');
  }

  public removeFromPreviousQuotes(): void {
    this.localStorageService.remove('fromPreviousQuotes');
  }

  public removeStorages(): void {
    this.localStorageService.remove('fromPreviousQuotes');
    this.localStorageService.remove('currentQuote');
    this.localStorageService.remove('quotePrices');
    this.localStorageService.remove('steps');
    this.localStorageService.remove('form');
    this.localStorageService.remove('quotes');
    this.sessionStorageService.remove('SECTION_PAGINATION_COBERTURA');
    this.sessionStorageService.remove('SECTION_PAGINATION_MATERIAASEGURADA');
    this.sessionStorageService.remove('TEMPORAL_COBERTURAS');
    this.sessionStorageService.remove('TEMPORAL_EQUIPAMENTOS');
    this.localStorageService.remove('productJSON');
    // Por si lo tiene en localstorage (versión anterior)
    this.localStorageService.remove('SECTION_PAGINATION_COBERTURA');
    this.localStorageService.remove('SECTION_PAGINATION_MATERIAASEGURADA');
    this.localStorageService.remove('TEMPORAL_COBERTURAS');
    this.localStorageService.remove('TEMPORAL_EQUIPAMENTOS');
    this.setQuote();
  }

  public updateStepForm(quoteForm: FormGroup): void {
    if (this.quote) quoteForm.patchValue(this.quote);
  }

  public getDataFromStep(quoteForm: FormGroup) {
    return {
      ...this.quote,
      ...quoteForm.value,
    };
  }

  public updateStepCurrentCode(code: string): void {
    this.validateForm({
      ...this.quote,
      currentStepCode: code,
    });
  }

  public getControl(name: string) {
    const splitted = name?.split('.');
    if (!this.quote[splitted[0]]) return null;
    const finded = splitted.reduce((o, i) => {
      if (o) return o[i];
      return null;
    }, this.quote);
    return finded || null;
  }

  public removeItemFromArray(arrayName, key) {
    const quote = [...this.quote[arrayName]];
    quote.splice(key, 1);
    this.setQuote({ ...this.quote, [arrayName]: quote });
  }

  public getNumberItemsOfArray(arrayName): number {
    return this.quote[arrayName]?.length || 0;
  }

  public changeFlagHasModifications(value) {
    this.hasModifications$.next(value);
    this.localStorageService.set(PROPOSAL_HAS_MODIFICATIONS, value);
  }

  public async manageCreateProposalData(
    forms: any,
    type: string,
    ciiuList: any,
    proposalNumber?: string
  ) {
    const formCont = forms.controls['form0']['controls'];
    const dcNum0 =
      formCont.documentNumberSelect.value === 'notMatch'
        ? formCont.documentNumberInput.value
        : formCont.documentNumberSelect.value?.split('-')[1];
    const contractorFullName = `${formCont.name.value} ${formCont.lastName.value} ${formCont.secondLastName.value}`;

    const formInsured = forms.controls['form1']['controls'];
    const dcNum1 =
      formInsured.documentNumberSelect.value === 'notMatch'
        ? formInsured.documentNumberInput.value
        : formInsured.documentNumberSelect.value?.split('-')[1];
    const insuredFullName = `${formInsured.name.value} ${formInsured.lastName.value} ${formInsured.secondLastName.value}`;

    const formInter = forms.controls['formIntermediary']['controls'];
    const formNewIns = forms.controls['formNewInsured'];
    const formBeneficiary = forms.controls['formBeneficiary']['controls'];
    const firstCIIUCode = forms.value.ciiuList[0].descriptionCIIU;

    const payload: any = {
      infoBasic: {
        canalCode: forms.controls['canalCode'].value,
        productCode: '00',
        paymentCurrency: 'PEN',
        startOfValidityDate: moment(forms.value.formValidity.startDate)
          .format('YYYY/MM/DD')
          ?.split('/')
          .join('-'),
        endOfValidityDate: moment(forms.value.formValidity.finishDate)
          .format('YYYY/MM/DD')
          ?.split('/')
          .join('-'),
        daysDuration: forms.value.formValidity.days,
        pipedriveNumber: forms.value.pipedrive,
      },
      persons: [
        {
          personRelationType: 'CONT',
          documentType: formCont.documentType.value,
          documentValue: `${dcNum0}`,
          businessName:
            formCont.companyName.value === 'empty'
              ? contractorFullName
              : formCont.companyName.value,
          firstName: formCont.name.value === 'empty' ? null : formCont.name.value,
          secondName: null,
          lastName: formCont.lastName.value === 'empty' ? null : formCont.lastName.value,
          secondLastName:
            formCont.secondLastName.value === 'empty' ? null : formCont.secondLastName.value,
          address: formCont.street.value,
          addressType: '1',
          numberAddress: formCont.streetNumber.value,
          internalNumberAddress: forms.value.form0.reference,
          postalCode: '35164057',
          districtCode: formCont.districtCode.value,
          departmentCode: formCont.departmentCode.value,
          provinceCode: formCont.provinceCode.value,
          economicActivityPrincipalCode: firstCIIUCode,
          email: formCont.email.value,
          phone: formCont.phone.value,
          economicGroup: 'N/A',
          gender: 'M',
          emailBilling: formCont.billingMail.value,
          ciiuCodes: [],
          personType: forms.value.laftClient,
          companySize: forms.value.laftSize,
          companyType: forms.value.ppjjType || '',
          companyActivity: forms.value.laftActivity,
        },
      ],
    };

    if (forms.value.canalCode !== 'DI') {
      payload['intermediary'] = {
        documentType: formInter.documentTypeIntermediary.value,
        documentValue: formInter.documentNumberIntermediary.value?.split('-')[1],
        email: formInter.intermediaryMail.value,
        intermediaryType: formInter.intermediaryType.value,
      };
    }

    if (!forms.controls['questionsRadio1']['value']) {
      const dataInsured = {
        personRelationType: 'INSU',
        documentType: formInsured.documentType.value,
        documentValue: `${dcNum1}`,
        businessName:
          formInsured.companyName.value === 'empty'
            ? insuredFullName
            : formInsured.companyName.value,
        firstName: formInsured.name.value === 'empty' ? null : formInsured.name.value,
        secondName: null,
        lastName: formInsured.lastName.value === 'empty' ? null : formInsured.lastName.value,
        secondLastName:
          formInsured.secondLastName.value === 'empty' ? null : formInsured.secondLastName.value,
        address: formInsured.street.value,
        addressType: '1',
        numberAddress: formInsured.streetNumber.value,
        internalNumberAddress: forms.value.form1.reference,
        postalCode: '35164057',
        districtCode: formInsured.districtCode.value,
        departmentCode: formInsured.departmentCode.value,
        provinceCode: formInsured.provinceCode.value,
        economicActivityPrincipalCode: firstCIIUCode,
        email: formInsured.email.value,
        phone: formInsured.phone.value,
        economicGroup: 'N/A',
        gender: 'M',
        ciiuCodes: [],
        insurableInterest: formInsured.insurableInterest.value,
        emailBilling: null,
        relationWithCont: formInsured.contractorRelation.value,
        personType: 'J',
      };

      forms.value.ciiuList.forEach((ciiu: any) => {
        const info = ciiuList.find((e: any) => e.key.includes(ciiu.descriptionCIIU));
        dataInsured.ciiuCodes.push({
          ciiuCode: info.key,
          ciiuDescription: info.name?.split(' | ')[1],
        });
      });
      payload.persons.push(dataInsured);
    } else {
      const infoContractor = {
        personRelationType: 'INSU',
        documentType: formCont.documentType.value,
        documentValue: `${dcNum0}`,
        businessName:
          formCont.companyName.value === 'empty' ? contractorFullName : formCont.companyName.value,
        firstName: formCont.name.value === 'empty' ? null : formCont.name.value,
        secondName: null,
        lastName: formCont.lastName.value === 'empty' ? null : formCont.lastName.value,
        secondLastName:
          formCont.secondLastName.value === 'empty' ? null : formCont.secondLastName.value,
        address: formCont.street.value,
        addressType: '1',
        numberAddress: formCont.streetNumber.value,
        internalNumberAddress: forms.value.form0.reference,
        postalCode: '35164057',
        districtCode: formCont.districtCode.value,
        departmentCode: formCont.departmentCode.value,
        provinceCode: formCont.provinceCode.value,
        economicActivityPrincipalCode: firstCIIUCode,
        email: formCont.email.value,
        phone: formCont.phone.value,
        economicGroup: 'N/A',
        gender: 'M',
        emailBilling: formCont.billingMail.value,
        ciiuCodes: [],
        personType: 'J',
      };

      forms.value.ciiuList.forEach((ciiu: any) => {
        const info = ciiuList.find((e: any) => e.key.includes(ciiu.descriptionCIIU));
        infoContractor.ciiuCodes.push({
          ciiuCode: info.key,
          ciiuDescription: info.name?.split(' | ')[1],
        });
      });

      payload.persons.push(infoContractor);
    }

    if (formNewIns) {
      const insured = payload.persons[1];
      insured['insuredAdditional'] = formNewIns.controls.newInsured.value;
      insured['relationWithInsu'] = formNewIns.controls.insuredRelation.value;
    }
    if (forms.value.formBeneficiary) {
      const data = {
        personRelationType: 'BENE',
        personType: 'J',
        documentType: '',
        documentValue: '',
        businessName: '',
        firstName: '',
        lastName: '',
        secondLastName: '',
        address: 'N/A',
        numberAddress: null,
        districtCode: forms.value.formBeneficiary.districtCode,
        departmentCode: forms.value.formBeneficiary.departmentCode,
        provinceCode: forms.value.formBeneficiary.provinceCode,
        email: null,
        phone: null,
        secondName: null,
        addressType: '1',
        internalNumberAddress: 'N/A',
        postalCode: '35164057',
        economicActivityPrincipalCode: firstCIIUCode,
        economicGroup: 'N/A',
        gender: 'M',
        ciiuCodes: [],
        insurableInterest: null,
        emailBilling: null,
        beneficiaryType: forms.value.formBeneficiary.beneficiaryType,
        relationWithInsu: forms.value.formBeneficiary.insuredRelation || null,
        companyCondition: forms.value.formBeneficiary.laftCondition,
      };

      if (forms.value.formBeneficiary.beneficiaryType === '2') {
        data.documentType = formCont.documentType.value;
        data.documentValue = dcNum0;
        data.businessName =
          formCont.companyName.value === 'empty' ? contractorFullName : formCont.companyName.value;
        data.firstName = formCont.name.value === 'empty' ? null : formCont.name.value;
        data.lastName = formCont.lastName.value === 'empty' ? null : formCont.lastName.value;
        data.secondLastName =
          formCont.secondLastName.value === 'empty' ? null : formCont.secondLastName.value;
      } else if (forms.value.formBeneficiary.beneficiaryType === '1') {
        data.documentType = formBeneficiary.documentType.value;
        data.documentValue = formBeneficiary.documentNumber.value;
        data.businessName =
          formBeneficiary.socialRazon.value === 'empty'
            ? contractorFullName
            : formBeneficiary.socialRazon.value;

        data.firstName = formBeneficiary.name.value === 'empty' ? null : formBeneficiary.name.value;
        data.lastName =
          formBeneficiary.firstLastName.value === 'empty'
            ? null
            : formBeneficiary.firstLastName.value;
        data.secondLastName =
          formBeneficiary.secondLastName.value === 'empty'
            ? null
            : formBeneficiary.secondLastName.value;
      } else {
        data.documentType = forms.value.formBeneficiary.documentType;
        data.documentValue = forms.value.formBeneficiary.documentNumber;
        data.businessName =
          forms.value.formBeneficiary.socialRazon === 'empty'
            ? forms.value.formBeneficiary.name
            : forms.value.formBeneficiary.socialRazon;
        data.firstName =
          forms.value.formBeneficiary.name === 'empty' ? null : forms.value.formBeneficiary.name;
        data.lastName =
          forms.value.formBeneficiary.lastName === 'empty'
            ? null
            : forms.value.formBeneficiary.lastName;
        data.secondLastName =
          forms.value.formBeneficiary.secondLastName === 'empty'
            ? null
            : forms.value.formBeneficiary.secondLastName;
      }

      payload.persons.push(data);
    }

    try {
      if (type === 'create') {
        const result = await lastValueFrom(this.proposalService.createAplication(payload));
        return result;
      }
      return await this.updateAppOrProp(payload, 'PE', proposalNumber);
    } catch (error) {
      console.log(error);

      return false;
    }
  }

  public getPrefirmExtraDocs(extraFiles: any, codigo: string) {
    const data = {
      filesData: [],
    };

    if (Array.isArray(extraFiles)) {
      extraFiles?.forEach((e: any) => {
        if (e.file !== '') {
          data.filesData.push({
            field: e.key,
            filename: e.file.name,
            file: {},
          });
        }
      });
    } else {
      data.filesData.push(extraFiles);
    }

    // if(data.filesData.length === 0 ) return 'noFiles'
    const result = this.proposalService.presignExtraFiles(data, codigo);
    return result;
  }

  async doUploadSignedUrl(signedUrl: string, file: File) {
    return new Promise((resolve, reject) => {
      const xhr = new XMLHttpRequest();
      xhr.open('PUT', signedUrl, true);
      xhr.onload = function onLoad() {
        if (xhr.status >= 200 && xhr.status < 300) {
          resolve(xhr.response);
        } else {
          reject(new Error(xhr.statusText));
        }
      };
      xhr.onerror = function onError() {
        reject(new Error(xhr.statusText));
      };
      xhr.send(file);
    });
  }

  async sendFiles(extraFiles: any, codigo: string, proposalNumber: string) {
    const filesToUpload = {
      applicationNumber: proposalNumber,
      files: [],
    };
    extraFiles?.forEach((e: any) => filesToUpload.files.push(e));
    const result: any = await lastValueFrom(this.proposalService.sendFiles(filesToUpload, codigo));
  }

  removeInvisibleCharacters(inputString: string) {
    const normalizedString = inputString.normalize('NFD');
    const stringWithoutTildes = normalizedString.replace(/[\u0300-\u036f]/g, '');
    const invisibleCharsRegex = /[\u200B-\u200D\uFEFF\u00A0\s]/g;
    const resultString = stringWithoutTildes.replace(invisibleCharsRegex, ' ');
    return resultString.toLowerCase();
  }

  deleteCode(inputString: string) {
    const regex = /^[0-9]{2}\s/;
    const resultado = inputString.replace(regex, '');
    return resultado.toLowerCase();
  }

  public async updateProposalPE(
    locals: any,
    codigo: string,
    proposalNumber: any,
    streets: any,
    uses: any,
    informationAditional: any,
    dynamicOps: any,
    conjuntoHabitacional: any,
    tipoDeInterior: any,
    tipoDeSubdivision: any,
    tipoEstructuraEspecial: any,
    dubaiDataAdittional: any,
    lucroCesantePeriodo: any
  ) {
    const data: any = {
      infoBasic: null,
      payment: null,
      insuredMatters: [],
      coverages: null,
      // customInsuredMatters: informationAditional,
      dubaiDataAdittional: {
        dubaiCurrencyType: dubaiDataAdittional[0],
        isNewDubai: dubaiDataAdittional[1],
        period: lucroCesantePeriodo,
      },
    };

    locals.forEach((local: any) => {
      const findStreet = streets?.find(
        (street: any) => this.removeInvisibleCharacters(street.value) === local['4'].toLowerCase()
      );

      const useformat = Number(local['32']?.split(' ')[0]);
      const findUse = uses.find((uses: any) => uses.key === useformat.toString());

      let findTipoDeEstrcuturaEspecial = null;
      if (local['28']) {
        const parseTEP = local['28'] === 'No aplica' ? 'no aplica' : this.deleteCode(local['28']);
        findTipoDeEstrcuturaEspecial = tipoEstructuraEspecial.find(
          (e: any) =>
            this.removeInvisibleCharacters(e.value) === this.removeInvisibleCharacters(parseTEP)
        );
      }

      const infoToSend = {
        tipoDeCalle: findStreet.key,
        nombreDeCalle: local['5'],
        numero: `${local['6']}`,
        pais: local['17'],
        departamentoRegion: local['18'],
        provincia: local['19'],
        distrito: local['20'],
        ubigeo: local['21'].result,
        latitudX: `${local['22'].result}`,
        longitudY: `${local['23'].result}`,
        anioConstruccion: `${local['25']}`,
        tipoMaterialConstruccionPredominante: local['27']?.split('.')[0],
        TEPTipoEstructuraEspecial: findTipoDeEstrcuturaEspecial.key,
        TEENumeroPisosEdificacion: `${local['29']}`,
        numeroSotanosEdificacion: `${local['30']}`,
        tipoBienAsegurado: local['31'],
        tipoDeUso: findUse.key,
        giroDelInmueble: local['33'],
        COD_CIIU_EmpresaActividad: `${local['34']}`,
        descripcionCIIU: local['35'].result,
        edificacion: local['37'] || 0,
        totalEdificacion: local['39'].result || 0,
        existencias: local['40'] || 0,
        equiposElectronicos: local['41'] || 0,
        maquinariaFija: local['42'] || 0,
        maquinariaEquipoMovil: local['43'] || 0,
        contenidoEnGeneral: local['44'] || 0,
        totalContenido: local['46'].result || 0,
        totalLucroCesante: local['48'].result || 0,
        valorDeclaradoTotal: local['49'].result || 0,
        mueblesUtilesEnseres: 0.0,
      };

      let findTipoDeInterior = null;
      if (local['7']) {
        findTipoDeInterior = tipoDeInterior.find(
          (e: any) => this.removeInvisibleCharacters(e.value) === local['7'].trim().toLowerCase()
        );
        infoToSend['tipoInterior'] = findTipoDeInterior.key;
      }
      if (local['8']) {
        infoToSend['interior'] = local['8']?.toString();
      }
      if (local['9']) {
        infoToSend['mz'] = local['9']?.toString();
      }
      if (local['10']) {
        infoToSend['lote'] = local['10']?.toString();
      }
      if (local['11']) {
        infoToSend['km'] = local['11']?.toString();
      }
      let findConjuntoHabitacional = null;
      if (local['12']) {
        findConjuntoHabitacional = conjuntoHabitacional.find(
          (e: any) =>
            this.removeInvisibleCharacters(e.value) ===
            this.removeInvisibleCharacters(local['12'].trim())
        );
        infoToSend['tipoConjuntoHabitacional'] = findConjuntoHabitacional.key;
      }
      if (local['13']) {
        infoToSend['nombreConjuntoHabitacional'] = local['13']?.toString();
      }
      let findTipoDeSubdivision = null;
      if (local['14']) {
        findTipoDeSubdivision = tipoDeSubdivision.find(
          (e: any) => this.removeInvisibleCharacters(e.value) === local['14'].toLowerCase()
        );
        infoToSend['subdivisionConjuntoHabitacional'] = findTipoDeSubdivision.key;
      }
      if (local['15']) {
        infoToSend['nombreDeSubdivisionDelConjuntoHabitacionalReferencia'] =
          local['15']?.toString();
      }
      if (local['16']) {
        infoToSend['anioReforzamientoEstructural'] = local['16']?.toString();
      }

      // tipoInterior: findTipoDeInterior.key || null,
      // interior: local['8']?.toString() || null,
      // mz: local['9']?.toString() || null,
      // lote: local['10']?.toString() || null,
      // km: local['11']?.toString() || null,
      // tipoConjuntoHabitacional: findConjuntoHabitacional.key || null,
      // nombreConjuntoHabitacional: local['13']?.toString() || null,
      // subdivisionConjuntoHabitacional: findTipoDeSubdivision.key || null,
      // nombreDeSubdivisionDelConjuntoHabitacionalReferencia: local['15']?.toString() || null,
      // anioReforzamientoEstructural: local['26']?.toString() || null,

      if (dynamicOps[0]) {
        if (dynamicOps[0] === 'SELECCIONAR OTRO (OPCIONAL)')
          infoToSend['otrosContenidos'] = local[45] || 0;
        if (dynamicOps[0] === 'ACERVO DOCUMENTARIO')
          infoToSend['acervoDocumentario'] = local[45] || 0;
        if (dynamicOps[0] === 'ANIMALES VIVOS') infoToSend['animalesVivos'] = local[45] || 0;
        if (dynamicOps[0] === 'BIENES REFRIGERADOS')
          infoToSend['bienesRefrigerados'] = local[45] || 0;
        if (dynamicOps[0] === 'CUADROS Y/U OBRAS DE ARTE')
          infoToSend['cuadrosObrasArte'] = local[45] || 0;
        if (dynamicOps[0] === 'DINERO Y/O VALORES') infoToSend['dineroValores'] = local[45] || 0;
        if (dynamicOps[0] === 'EMBARCACIONES EN DIQUE SECO')
          infoToSend['embarcacionesDiqueSeco'] = local[45] || 0;
        if (dynamicOps[0] === 'EQUIPOS ELECTRONICOS')
          infoToSend['equiposElectronicos'] = local[45] || 0;
        if (dynamicOps[0] === 'EQUIPOS MEDICOS') infoToSend['equiposMedicos'] = local[45] || 0;
        if (dynamicOps[0] === 'JOYAS') infoToSend['joyas'] = local[45] || 0;

        if (dynamicOps[0] === 'MAQUINARIA MOVIL Y EQUIPOS')
          infoToSend['maquinariaEquipoMovil'] = local[45] || 0;
        if (dynamicOps[0] === 'MOBILIARIO EN GENERAL')
          infoToSend['mobiliarioGeneral'] = local[45] || 0;
        if (dynamicOps[0] === 'MUEBLES , UTILES Y ENSERES')
          infoToSend['mueblesUtilesEnseres'] = local[45] || 0;
        if (dynamicOps[0] === 'OTROS CONTENIDOS') infoToSend['otrosContenidos'] = local[45] || 0;
        if (dynamicOps[0] === 'PLANTACIONES') infoToSend['plantaciones'] = local[45] || 0;
        if (dynamicOps[0] === 'REPUESTOS Y/O HERRAMIENTAS')
          infoToSend['repuestosHerramientas'] = local[45] || 0;
        if (dynamicOps[0] === 'VEHICULOS PROPIOS Y/O DE TERCEROS (COMO CONTENIDO)')
          infoToSend['vehiculosPropiosTerceros'] = local[45] || 0;
      }

      if (dynamicOps[1]) {
        if (dynamicOps[1] === 'AVISOS LUMINOSOS Y/O PANELES DE PUBLICIDAD')
          infoToSend['avisosLuminosos_panelesDePublicidad'] = local[38] || 0;
        if (dynamicOps[1] === 'CARRETERAS') infoToSend['carreteras'] = local[38] || 0;
        if (dynamicOps[1] === 'EDIFICACION (CUANDO SE ASEGURA A VALOR COMERCIAL)')
          infoToSend['edificacionValorComercial'] = local[38] || 0;
        if (dynamicOps[1] === 'INSTALACIONES FIJAS Y/O MEJORAS')
          infoToSend['instalacionesFijasMejoras'] = local[38] || 0;
        if (dynamicOps[1] === 'LINEAS DE TRANSMISION')
          infoToSend['lineasDeTransmision'] = local[38] || 0;
        if (dynamicOps[1] === 'MUELLES Y/O EMBARCADEROS')
          infoToSend['muellesEmbarcaderos'] = local[38] || 0;
        if (dynamicOps[1] === 'PISCINAS') infoToSend['piscina'] = local[38] || 0;
        if (dynamicOps[1] === 'PISTAS, CERCOS Y/O VEREDAS')
          infoToSend['pistasCercosVeredas'] = local[38] || 0;
        if (dynamicOps[1] === 'PUENTES') infoToSend['puentes'] = local[38] || 0;
        if (dynamicOps[1] === 'TANQUES') infoToSend['tanques'] = local[38] || 0;
        if (dynamicOps[1] === 'TORRES DE ILUMINACION')
          infoToSend['torresIluminacion'] = local[38] || 0;
        if (dynamicOps[1] === 'SELECCIONAR OTRO (OPCIONAL)')
          infoToSend['otrosEdificacion'] = local[38] || 0;
      }

      if (dynamicOps[2]) {
        if (dynamicOps[2] === 'Gastos estables (fijos)')
          infoToSend['gastosEstablesFijos'] = local[47] || 0;
        if (dynamicOps[2] === 'Beneficio Bruto (margen de contribución)')
          infoToSend['beneficioBruto'] = local[47] || 0;
      }

      data.insuredMatters.push(infoToSend);
    });

    if (informationAditional) {
      for (const extraKey in informationAditional) {
        const newKey = extraKey?.split('/');
        const localIndex = Number(newKey[1]) - 1;
        data.insuredMatters[localIndex][newKey[0]] = Number(informationAditional[extraKey]);

        if (newKey[0].includes('edificacion')) {
          data.insuredMatters[localIndex].totalEdificacion =
            data.insuredMatters[localIndex].totalEdificacion +
            Number(informationAditional[extraKey]);
        }
        if (newKey[0].includes('lucro')) {
          data.insuredMatters[localIndex].totalLucroCesante =
            data.insuredMatters[localIndex].totalLucroCesante +
            Number(informationAditional[extraKey]);
        }
        if (newKey[0].includes('contenido')) {
          data.insuredMatters[localIndex].totalContenido =
            data.insuredMatters[localIndex].totalContenido + Number(informationAditional[extraKey]);
        }
        data.insuredMatters[localIndex].valorDeclaradoTotal =
          data.insuredMatters[localIndex].valorDeclaradoTotal +
          Number(informationAditional[extraKey]);
      }
    }

    try {
      return await this.updateAppOrProp(data, codigo, proposalNumber);
    } catch (error) {
      console.log('err');
      return false;
    }

    // const result: any = this.proposalService.updateAplication(data, codigo, proposalNumber);
  }

  async updateAppOrProp(payload: any, countryCode: string, proposalNumber: string) {
    await lastValueFrom(this.proposalService.getProposalData(proposalNumber, countryCode));

    if (proposalNumber.charAt(0) !== 'P') {
      const { result } = await lastValueFrom(
        this.proposalService.updateAplication(payload, countryCode, proposalNumber)
      );

      return result;
    }
    const { result } = await lastValueFrom(
      this.proposalService.updateProposal(payload, countryCode, proposalNumber)
    );

    return result;
  }

  private addDatatoPolicyStorage(policyNumber, paymentCode) {
    const proposalData = this.localStorageService.get('proposalData');
    this.localStorageService.set('proposalData', {
      ...proposalData,
      policyNumber,
      paymentCode,
    });
  }

  async emitAppOrProp(proposalNumber) {
    await lastValueFrom(this.proposalService.getProposalData(proposalNumber, 'PE'));

    if (proposalNumber.charAt(0) !== 'P') {
      await lastValueFrom(
        this.proposalService.aplicationToProposal({ applicationNumber: proposalNumber })
      );
    } else {
      const { policyNumber, paymentCode } = await lastValueFrom(
        this.proposalService.proposalToPolicy({ proposalNumber })
      );
      this.addDatatoPolicyStorage(policyNumber, paymentCode);
    }
  }

  changeFlagHasDubaiChanged(value: boolean) {
    this.hasChangeDubai$.next(value);
  }
}
